import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  CancelToken,
  Method,
  ResponseType,
} from "axios";
import { AuthContext } from "../utility/useAuth";
import { User } from "oidc-client";
import { IOperation } from "../types/IOperation";
import { IJob } from "../types/IJob";
import { IConfig } from "../types/IConfig";
import { IUserInfo } from "../types/IUserInfo";
import { IJobDetails } from "../types/IJobDetails";
import { IBomDetails } from "../types/IBomDetails";
import { IPickedComponent } from "../types/IPickedComponent";
import { IRouting } from "../types/IRouting";
import { IStartJobRequest } from "../types/IStartJobRequest";
import { IJobLogging } from "../types/IJobLogging";
import { INonProductionJob } from "../types/INonProductionJob";
import { InspectionToolSet } from "../types/IInspectionToolSet";
import { InspectionStatus } from "../types/InspectionStatus";
import { ICompleteJobRequest } from "../types/ICompleteJobRequest";
import { InspectionLog } from "../types/InspectionLog";
import { LeakTestCertificate } from "../types/LeakTestCertificate";
import { WorkflowRequest } from "../types/WorkflowRequest";
import { WorkflowResponse } from "../types/WorkflowResponse";
import { EmailMessage } from "../types/EmailMessage";
import { IJobWithQtyOrdered } from "../types/IJobWithQtyOrdered";
import { IJobDataItem } from "../types/IJobDataItem";
import { PrintRequest } from "../types/PrintRequest";
import { Parameter } from "../types/Parameter";
import { ICheckList } from "../types/ICheckList";
import { Ticket } from "../types/reportChecklists/Ticket";
import { OperationFilter } from "../types/OperationFilter";
import { UserPreference } from "../types/UserPreference";
import { IssueDetails } from "../types/IssueDetails";
import { projectName, projectVersion } from "../config";

export const REQUEST_CANCELLED = "REQUEST_CANCELLED";

const encode = (id: string) => encodeURIComponent(encodeURIComponent(id));

const config = window.config as IConfig;

export const call = async <Response = any>( // eslint-disable-line
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  auth: AuthContext,
  payload?: any, // eslint-disable-line
  cancelToken?: CancelToken,
  responseType?: string,
): Promise<AxiosResponse<Response>> => {
  //return axios.request<Response>(requestConfig)

  const requestConfig: AxiosRequestConfig = {
    method: method as Method,
    url: `${path}`,
    data: payload,
    cancelToken,
    responseType: responseType as ResponseType,
  };

  return auth
    .getUser()
    .then((user: User | null) => {
      if (user && user.access_token) {
        axios.defaults.headers.common.Accept = "application/json";
        axios.defaults.headers.common.Authorization = `Bearer ${user.access_token}`;
        axios.defaults.headers["Application"] = `${projectName}`;
        axios.defaults.headers["Version"] = `${projectVersion}`;
        requestConfig.headers = {
          Accept: "application/json",
          Authorization: `Bearer ${user.access_token}`,
          "Application": `${projectName}`,
          "Version": `${projectVersion}`
        };

        return axios.request<Response>(requestConfig);
      }
      throw new Error("User has no access token");
    })
    .catch((error) => {
      if (!error.response) throw error; // Network error (disconnected)
      if (error.response.status === 401) {
        // 401 Unauthorized
        return auth.renewToken().then((renewedUser) => {
          const headers = {
            Accept: "application/json",
            Authorization: `Bearer ${renewedUser.access_token}`,
          };
          requestConfig.headers = headers;
          return axios.request<Response>(requestConfig);
        });
      }
      throw error;
    });
};

export async function getOperations(
  auth: AuthContext,
  filter?: string,
): Promise<IOperation[]> {
  const val = await call<IOperation[]>(
    `${config.bffRoot}api/operations${filter ? `?filter=${filter}` : ""}`,
    "GET",
    auth,
  );

  return val.data;
}

export async function getJobs(
  auth: AuthContext,
  warehouse?: string,
): Promise<IJob[]> {
  const val = await call<IJob[]>(
    `${config.bffRoot}api/jobs${warehouse ? `?warehouse=${warehouse}` : ""}`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getWarehouses(auth: AuthContext): Promise<string[]> {
  const val = await call<string[]>(
    `${config.bffRoot}api/warehouses`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getStartedJobByUser(auth: AuthContext): Promise<IJob> {
  const val = await call<IJob>(
    `${config.bffRoot}api/jobs?status=started`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getNonProductionJobs(
  auth: AuthContext,
): Promise<INonProductionJob[]> {
  const val = await call<INonProductionJob[]>(
    `${config.bffRoot}api/non-production-jobs`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getJobDetails(
  auth: AuthContext,
  jobNumber: string,
  dontCreate: boolean = false,
): Promise<IJobDetails> {
  const val = await call<IJobDetails>(
    `${config.bffRoot}api/jobs/${jobNumber}/details?dontcreate=${dontCreate}`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getBomDetails(
  auth: AuthContext,
  jobNumber: string,
): Promise<IBomDetails> {
  const val = await call<IBomDetails>(
    `${config.bffRoot}api/jobs/${jobNumber}/bom`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getRoutings(
  auth: AuthContext,
  jobNumber: string,
): Promise<IRouting[]> {
  const val = await call<IRouting[]>(
    `${config.bffRoot}api/jobs/${jobNumber}/routings`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getJobOperations(
  auth: AuthContext,
  jobNumber: string,
  status?: string,
): Promise<IJobLogging[]> {
  const val = await call<IJobLogging[]>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations${
      status ? `?status=${status}` : ""
    }`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getSimilarJobs(
  auth: AuthContext,
  jobNumber: string,
  status?: string,
): Promise<IJobWithQtyOrdered[]> {
  const val = await call<IJobWithQtyOrdered[]>(
    `${config.bffRoot}api/similar-jobs?job=${jobNumber}`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getJobDataItems(
  auth: AuthContext,
): Promise<IJobDataItem[]> {
  const val = await call<IJobDataItem[]>(
    `${config.bffRoot}api/job-data-items`,
    "GET",
    auth,
  );
  return val.data;
}

export async function updateBomDetails(
  auth: AuthContext,
  jobNumber: string,
  components: IPickedComponent[],
): Promise<any> {
  const val = await call<IBomDetails>(
    `${config.bffRoot}api/jobs/${jobNumber}/bom`,
    "POST",
    auth,
    components,
  );
  return val;
}

export async function startOperation(
  auth: AuthContext,
  jobNumber: string,
  operation: string,
  request: IStartJobRequest,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations/${encode(
      operation,
    )}/start`,
    "POST",
    auth,
    request,
  );
  return val;
}

export async function stopOperation(
  auth: AuthContext,
  jobNumber: string,
  operation: string,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations/${encode(
      operation,
    )}/stop`,
    "POST",
    auth,
  );
  return val;
}

export async function deleteOperation(
  auth: AuthContext,
  jobNumber: string,
  operation: string,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations/${encode(
      operation,
    )}/delete`,
    "POST",
    auth,
  );
  return val;
}

export async function reworkOperation(
  auth: AuthContext,
  jobNumber: string,
  operation: string,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations/${encode(
      operation,
    )}/rework`,
    "POST",
    auth,
  );
  return val;
}

export async function completeOperation(
  auth: AuthContext,
  jobNumber: string,
  operation: string,
  request: ICompleteJobRequest,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations/${encode(
      operation,
    )}/complete`,
    "POST",
    auth,
    request,
  );
  return val;
}

export async function completeProductionOrder(
  auth: AuthContext,
  jobNumber: string,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/complete`,
    "POST",
    auth,
  );
  return val;
}

export async function holdProductionOrder(
  auth: AuthContext,
  jobNumber: string,
  reason: string,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/hold`,
    "POST",
    auth,
    reason,
  );
  return val;
}

export async function unholdProductionOrder(
  auth: AuthContext,
  jobNumber: string,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/hold`,
    "DELETE",
    auth,
  );
  return val;
}

export async function getJobNote(
  auth: AuthContext,
  jobNumber: string,
): Promise<string> {
  const val = await call<string>(
    `${config.bffRoot}api/jobs/${jobNumber}/note`,
    "GET",
    auth,
  );
  return val.data;
}

export async function updateJobNote(
  auth: AuthContext,
  jobNumber: string,
  jobNote: string,
): Promise<any> {
  const val = await call<IBomDetails>(
    `${config.bffRoot}api/jobs/${jobNumber}/note`,
    "POST",
    auth,
    jobNote,
  );
  return val;
}

export async function getCurrentUser(auth: AuthContext): Promise<IUserInfo> {
  const val = await call<IUserInfo>(
    `${config.bffRoot}api/users/current`,
    "GET",
    auth,
  );

  return val.data;
}

export async function getUsers(auth: AuthContext): Promise<IUserInfo[]> {
  const val = await call<IUserInfo[]>(
    `${config.bffRoot}api/users`,
    "GET",
    auth,
  );

  return val.data;
}

export async function getBankHolidays(auth: AuthContext): Promise<Date[]> {
  const val = await call<Date[]>(
    `${config.bffRoot}api/bank-holidays`,
    "GET",
    auth,
  );

  return val.data;
}

export async function getInspectionToolSets(
  auth: AuthContext,
  operation: string,
): Promise<InspectionToolSet[]> {
  const val = await call<InspectionToolSet[]>(
    `${config.bffRoot}api/operations/${operation}/inspection-toolsets`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getInspectionReasons(
  auth: AuthContext,
  operation: string,
  status: InspectionStatus,
): Promise<string[]> {
  const val = await call<string[]>(
    `${config.bffRoot}api/operations/${operation}/inspection-reasons?status=${InspectionStatus[status]}`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getJobInspectionLogs(
  auth: AuthContext,
  job: string,
  status: InspectionStatus,
): Promise<InspectionLog[]> {
  const val = await call<InspectionLog[]>(
    `${config.bffRoot}api/jobs/${job}/inspection-logs?status=${InspectionStatus[status]}`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getLeakTestCertificates(
  auth: AuthContext,
): Promise<LeakTestCertificate[]> {
  const val = await call<LeakTestCertificate[]>(
    `${config.bffRoot}api/leak-test-certificates`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getLeakTestCertificatesForProductionOrder(
  auth: AuthContext,
  job: string,
): Promise<LeakTestCertificate[]> {
  const val = await call<LeakTestCertificate[]>(
    `${config.bffRoot}api/jobs/${job}/leak-test-certificates`,
    "GET",
    auth,
  );
  return val.data;
}

export async function saveLeakTestCertificate(
  auth: AuthContext,
  certificate: LeakTestCertificate,
): Promise<LeakTestCertificate> {
  const val = await call<LeakTestCertificate>(
    `${config.bffRoot}api/leak-test-certificates`,
    "POST",
    auth,
    certificate,
  );
  return val.data;
}

export async function executeWorkflow(
  auth: AuthContext,
  jobNumber: string,
  operation: string,
  action: string,
  request: WorkflowRequest,
): Promise<WorkflowResponse> {
  const val = await call<any>(
    `${config.bffRoot}api/jobs/${jobNumber}/operations/${encode(
      operation,
    )}/${action}/evaluate`,
    "POST",
    auth,
    request,
  );
  return val.data;
}

export async function sendEmail(
  auth: AuthContext,
  emailMessage: EmailMessage,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/emails`,
    "POST",
    auth,
    emailMessage,
  );
  return val;
}

export async function getDocuments(
  auth: AuthContext,
  prefix?: string,
  folder?: string,
  notcached: boolean = false
): Promise<string[]> {
  const val = await call<string[]>(
    `${config.bffRoot}api/documents${folder ? "/" + folder : ""}${"?notcached=" + notcached}${
      prefix ? "&prefix=" + prefix : ""
    }`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getDocumentContent(
  auth: AuthContext,
  folder: string,
  documentName: string,
  thumbnail?: boolean,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/documents/${folder}/${documentName}/${
      thumbnail === true ? "?thumbnail=true" : ""
    }`,
    "GET",
    auth,
    null,
    undefined,
    "blob",
  );
  return val.data;
}

export async function getDownload(
  auth: AuthContext,
  version: string
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/downloads/${version}`,
    "GET",
    auth,
    null,
    undefined,
    "blob",
  );
  return val.data;
}

export async function getTemplateParameters(
  auth: AuthContext,
  folder: string,
  documentName: string,
  id: string,
): Promise<Parameter[]> {
  const val = await call<Parameter[]>(
    `${config.bffRoot}api/documents/${folder}/${documentName}/template-parameters?id=${id}`,
    "GET",
    auth,
  );
  return val.data;
}

export async function printTemplate(
  auth: AuthContext,
  request: PrintRequest,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/print`,
    "POST",
    auth,
    request,
    undefined,
    "blob",
  );
  return val.data;
}

export async function printTemplateAsPdf(
  auth: AuthContext,
  request: PrintRequest,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/print/pdf`,
    "POST",
    auth,
    request,
    undefined,
    "blob",
  );
  return val.data;
}

export async function deleteDocument(
  auth: AuthContext,
  folder: string,
  documentName: string,
): Promise<any> {
  const val = await call<string[]>(
    `${config.bffRoot}api/documents/${folder}/${documentName}`,
    "DELETE",
    auth,
  );
  return val;
}

export async function uploadDocument(
  auth: AuthContext,
  folder: string,
  documentName: string,
  documentContent: Blob,
): Promise<any> {
  const formData = new FormData();
  formData.append("file", documentContent);
  const val = await call<string[]>(
    `${config.bffRoot}api/documents/${folder}/${documentName}`,
    "POST",
    auth,
    formData,
  );
  return val;
}

export async function getOption(
  auth: AuthContext,
  option: string,
): Promise<string> {
  const val = await call<string>(
    `${config.bffRoot}api/options/${option}`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getOptions(
  auth: AuthContext,
  option: string,
): Promise<string[]> {
  const val = await call<string[]>(
    `${config.bffRoot}api/options?option=${option}`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getProjectFolder(
  auth: AuthContext,
  stockCode: string,
): Promise<string> {
  const val = await call<string>(
    `${config.bffRoot}api/project-folders/${stockCode}`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getCheckList(
  auth: AuthContext,
  checkList: string,
): Promise<ICheckList> {
  const val = await call<ICheckList>(
    `${config.bffRoot}api/checklists/${checkList}`,
    "GET",
    auth,
  );
  return val.data;
}

export async function getPurchaseOrders(auth: AuthContext): Promise<string[]> {
  const val = await call<string[]>(
    `${config.bffRoot}api/purchase-orders`,
    "GET",
    auth,
  );
  return val.data;
}

//CreateTicket
export async function createTicket(
  auth: AuthContext,
  ticket: Ticket,
): Promise<string> {
  const val = await call<string>(
    `${config.bffRoot}api/tickets`,
    "POST",
    auth,
    ticket,
  );
  return val.data;
}

export async function lockAction(
  auth: AuthContext,
  action: string,
  maxminutes?: number,
): Promise<any> {
  const val = await call<string>(
    `${config.bffRoot}api/actions/${action}${
      maxminutes ? `?maxminutes=${maxminutes}` : ""
    }`,
    "POST",
    auth,
  );
  return val;
}

export async function unlockAction(
  auth: AuthContext,
  action: string,
): Promise<any> {
  const val = await call<string>(
    `${config.bffRoot}api/actions/${action}`,
    "DELETE",
    auth,
  );
  return val;
}

export async function getOperationFilters(
  auth: AuthContext,
): Promise<OperationFilter[]> {
  const val = await call<OperationFilter[]>(
    `${config.bffRoot}api/operation-filters`,
    "GET",
    auth,
  );
  return val.data;
}

export async function saveOperationFilters(
  auth: AuthContext,
  filters: OperationFilter[],
): Promise<any> {
  const val = await call<OperationFilter[]>(
    `${config.bffRoot}api/operation-filters`,
    "POST",
    auth,
    filters,
  );
  return val;
}

export async function getUserPreference(
  auth: AuthContext,
): Promise<UserPreference> {
  const val = await call<UserPreference>(
    `${config.bffRoot}api/users/current/preference`,
    "GET",
    auth,
  );
  return val.data;
}

export async function setUserPreference(
  auth: AuthContext,
  filters: UserPreference,
): Promise<any> {
  const val = await call<UserPreference>(
    `${config.bffRoot}api/users/current/preference`,
    "POST",
    auth,
    filters,
  );
  return val;
}

export async function reportIssue(
  auth: AuthContext,
  issue: IssueDetails,
): Promise<any> {
  const val = await call<any>(
    `${config.bffRoot}api/issues`,
    "POST",
    auth,
    issue,
  );
  return val;
}


